import { formatParisDateTime } from '@flux/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useState, type JSX } from 'react';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { flushFileQueue } from '@/features/settings/api-martelet.js';
import { useMaintenanceState } from '@/features/settings/use-maintenance-state.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';

/**
 * Bandeau global (`02` §0) — refonte du 10/09/2026 : il dit ce qui se passe
 * (« envois suspendus »), depuis quand, pourquoi, et **combien de fichiers
 * attendent**. C'est ce dernier chiffre qui décide s'il faut agir.
 *
 * Deux issues directes : envoyer la file sans lever la suspension, ou aller la
 * gérer. « Mode maintenance actif » ne disait ni ce qui était arrêté, ni ce
 * qu'il fallait en faire.
 */
export function MaintenanceBanner(): JSX.Element | null {
  const queryClient = useQueryClient();
  const { data } = useMaintenanceState();
  const [flushOpen, setFlushOpen] = useState(false);

  const flushMutation = useMutation({
    mutationFn: flushFileQueue,
    onSuccess: (result) => {
      toast.success(`${String(result.queuedCount)} fichier(s) remis en file d’envoi.`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings.maintenance() });
      setFlushOpen(false);
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  if (!data?.enabled) {
    return null;
  }

  const since = data.since ? formatParisDateTime(new Date(data.since)) : null;

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-status-red-border bg-status-red-bg px-4 py-2 text-sm text-status-red-fg md:px-8"
    >
      <span className="flex min-w-0 flex-1 items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          <span className="font-semibold">Envois vers Martelet suspendus</span>
          {since ? ` depuis le ${since}` : ''}
          {data.reason ? ` (${data.reason})` : ''} —{' '}
          <span className="font-medium tabular-nums">{data.queuedCount} fichier(s) en attente</span>
          .
        </span>
      </span>
      <span className="flex items-center gap-2">
        {data.queuedCount > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setFlushOpen(true);
            }}
          >
            Envoyer maintenant
          </Button>
        ) : null}
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="text-status-red-fg hover:bg-status-red-border/60 [&_svg]:text-status-red-fg"
        >
          <Link to="/parametres" search={{ tab: 'martelet' }}>
            Gérer
          </Link>
        </Button>
      </span>

      <ConfirmDialog
        open={flushOpen}
        onOpenChange={setFlushOpen}
        title="Envoyer les fichiers en attente ?"
        description={`${String(data.queuedCount)} fichier(s) partiront immédiatement chez Martelet, sans lever la suspension.`}
        confirmLabel="Envoyer"
        isConfirming={flushMutation.isPending}
        onConfirm={() => {
          flushMutation.mutate();
        }}
      />
    </div>
  );
}
